import { useEffect, useRef, useState, KeyboardEvent } from 'react';
import { ChatBubble, ThinkingDots } from './ChatBubble';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface Props {
  messages: ChatMessage[];
  loading: boolean;
  onSend: (message: string) => void;
}

export function ChatPanel({ messages, loading, onSend }: Props) {
  const [text, setText] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length, loading]);

  const submit = () => {
    if (!text.trim() || loading) return;
    onSend(text.trim());
    setText('');
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="flex flex-col h-full bg-surface border border-border rounded-lg">
      <div className="px-4 py-3 border-b border-border-light">
        <div className="text-sm font-medium text-text-primary">AI Builder</div>
        <div className="text-xs text-text-muted">Describe a report in plain English</div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3">
        {messages.length === 0 && !loading && (
          <div className="text-xs text-text-muted">
            Try: "Patients with balances over $500 in the last 60 days, by carrier."
          </div>
        )}
        {messages.map((m, i) => <ChatBubble key={i} role={m.role} content={m.content} />)}
        {loading && <ThinkingDots />}
      </div>

      <div className="p-3 border-t border-border-light">
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={onKey}
          placeholder="Describe a report…"
          rows={2}
          className="w-full px-2.5 py-1.5 text-sm bg-surface border border-border rounded-md focus:outline-none focus:border-accent resize-none"
        />
        <button
          onClick={submit}
          disabled={!text.trim() || loading}
          className="mt-2 w-full bg-accent hover:bg-accent-dark disabled:opacity-50 disabled:cursor-not-allowed text-white py-1.5 rounded-md text-sm font-medium"
        >
          Send
        </button>
      </div>
    </div>
  );
}
